import { useMemo } from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/shared/widgets/carousel';
import { resolveImageUrl } from '@/shared/utils/imageUtils';
import { getCenterSlideIndex } from '@/shared/utils/carouselUtils';
import { TAILWIND_MD_MIN_PX } from '@/shared/constants/breakpoints';
import { AnalyticsBannerLink } from '@/shared/features/analytics/presentation/components';
import { AppLink } from '@/shared/components/AppLink';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';

interface CollectionImage {
  width: number;
  url: string;
}

interface CollectionItem {
  title: string;
  images?: CollectionImage[];
  imageUrl?: string;
  link: string;
  priority?: number;
  position?: number;
  isVideo?: boolean;
  videoUrl?: string;
  autoplay?: string;
  playsinline?: string;
  controls?: string;
  loop?: string;
  muted?: string;
  shouldRefresh?: boolean;
}

interface CollectionSectionProps {
  data: Record<string, unknown> & {
    title?: string[];
    layoutRows?: number;
    btn?: string;
    link?: string;
    shouldRefresh?: boolean;
    blankImageUrl?: string;
    items?: CollectionItem[];
    grouping?: number[][];
    groupingDesktop?: number[]; // Flat array of priorities for desktop (e.g., [3,1,2])
    showTitle?: boolean;
  };
  sectionKey: string;
  eventLabel?: string;
  priority?: number;
}

type CollectionCardVariant = 'desktop' | 'mobile';

function CollectionCardContent({
  item,
  variant,
}: {
  item: CollectionItem;
  variant: CollectionCardVariant;
}) {
  const isDesktop = variant === 'desktop';
  const linkClassName = isDesktop
    ? 'relative group hover:opacity-95 transition-opacity block'
    : 'block relative group hover:opacity-90 transition-opacity';

  const videoBlock = item.isVideo && item.videoUrl && (
    <div className={isDesktop ? 'relative w-full' : 'relative w-full aspect-[4/3] overflow-hidden rounded-lg'}>
      <video
        poster={
          item.images?.[0]
            ? resolveImageUrl(item.images[0].url)
            : item.imageUrl
              ? resolveImageUrl(item.imageUrl)
              : undefined
        }
        className={isDesktop ? 'w-full h-auto object-cover rounded-lg' : 'w-full h-full object-cover'}
        title={item.title}
        width="100%"
        height="auto"
        autoPlay={!!item.autoplay}
        playsInline={!!item.playsinline}
        controls={!!item.controls}
        loop={!!item.loop}
        muted={!!item.muted}
      >
        <source src={item.videoUrl} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      {isDesktop && (
        <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/60 to-transparent rounded-b-lg">
          <h3 className="text-white font-primary text-2xl md:text-3xl font-normal mb-1">
            {item.title}
          </h3>
        </div>
      )}
    </div>
  );

  const imagesBlock = item.images && item.images.length > 0 && (
    <div className={isDesktop ? 'relative w-full pb-2' : 'relative w-full aspect-[4/3] overflow-hidden rounded-lg bg-gray-100 shadow-[0_3px_3px_rgba(0,0,0,0.25)]'}>
      <img
        className={isDesktop ? 'w-full h-auto object-cover rounded-lg block shadow-[0_3px_3px_rgba(0,0,0,0.25)]' : 'w-full h-full object-cover'}
        src={resolveImageUrl(item.images[0].url)}
        srcSet={item.images
          .map((img) => `${resolveImageUrl(img.url)} ${img.width}w`)
          .join(', ')}
        sizes={isDesktop ? `(min-width: ${TAILWIND_MD_MIN_PX}px) 33vw, 100vw` : '80vw'}
        alt={item.title}
        loading="lazy"
      />
    </div>
  );

  const imageUrlBlock = item.imageUrl && !item.images?.length && (
    <div className={isDesktop ? 'relative w-full pb-2' : 'relative w-full aspect-[4/3] overflow-hidden rounded-lg bg-gray-100 shadow-[0_3px_3px_rgba(0,0,0,0.25)]'}>
      <img
        className={isDesktop ? 'w-full h-auto object-cover rounded-lg block shadow-[0_3px_3px_rgba(0,0,0,0.25)]' : 'w-full h-full object-cover'}
        src={resolveImageUrl(item.imageUrl)}
        sizes={isDesktop ? `(min-width: ${TAILWIND_MD_MIN_PX}px) 33vw, 100vw` : '80vw'}
        alt={item.title}
        loading="lazy"
      />
    </div>
  );

  const fallback = isDesktop && (
    <div className="w-full aspect-video bg-gray-200 rounded-lg flex items-center justify-center">
      <span className="text-gray-400">No image</span>
    </div>
  );

  return (
    <AnalyticsBannerLink
      to={item.link}
      bannerName={item.title}
      bannerType="collections"
      fromNavBar={false}
      shouldRefresh={item.shouldRefresh}
      className={linkClassName}
    >
      {videoBlock || imagesBlock || imageUrlBlock || fallback}
    </AnalyticsBannerLink>
  );
}

export default function CollectionSection({
  data,
  sectionKey: _sectionKey,
  eventLabel: _eventLabel,
}: CollectionSectionProps) {
  const items = useMemo(() => (data.items || []) as CollectionItem[], [data.items]);
  const layoutRows = data.layoutRows || 2;
  const title = data.title?.[0] || '';
  const link = data.link || '#';
  const showTitle = data.showTitle !== false;

  // Group items based on grouping array - each group represents a slide with items stacked vertically
  const groupedItems = useMemo(() => {
    if (!items || items.length === 0) return [];

    // Grouping array defines which items go together in each slide
    // Each inner array represents priorities/positions for one slide
    // Items are stacked vertically (first item in array = top, second = bottom)
    if (data.grouping && Array.isArray(data.grouping) && data.grouping.length > 0) {
      const grouped: CollectionItem[][] = [];
      
      data.grouping.forEach((group) => {
        const groupItems: CollectionItem[] = [];
        group.forEach((priority) => {
          // Find item by priority
          const item = items.find((item) => item.priority === priority);
          if (item) {
            groupItems.push(item);
          }
        });
        if (groupItems.length > 0) {
          grouped.push(groupItems);
        }
      });
      
      return grouped;
    }

    // Fallback: group items in chunks of layoutRows if no grouping specified
    const grouped: CollectionItem[][] = [];
    for (let i = 0; i < items.length; i += layoutRows) {
      grouped.push(items.slice(i, i + layoutRows));
    }
    return grouped;
  }, [items, data.grouping, layoutRows]);

  // Calculate initial slide index (center slide)
  const initialSlideIndex = useMemo(() => getCenterSlideIndex(groupedItems.length), [groupedItems.length]);
  const { analytics, isInitialized } = useAnalytics();

  // Get desktop items using groupingDesktop (latest 3 banners)
  const desktopItems = useMemo(() => {
    if (!items || items.length === 0) return [];
    
    // Use groupingDesktop if available (flat array of priorities: [3,1,2])
    if (data.groupingDesktop && Array.isArray(data.groupingDesktop) && data.groupingDesktop.length > 0) {
      const desktopGrouped: CollectionItem[] = [];
      
      // groupingDesktop is a flat array of priorities for the 3 items to show
      data.groupingDesktop.forEach((priority: number) => {
        // Find item by priority
        const item = items.find((item) => item.priority === priority);
        if (item && !desktopGrouped.find((existing) => existing.title === item.title)) {
          desktopGrouped.push(item);
        }
      });
      
      // Return only the latest 3 items
      return desktopGrouped.slice(0, 3);
    }
    
    // Fallback: get last 3 items from items array
    return items.slice(-3);
  }, [items, data.groupingDesktop]);

  if (!items || items.length === 0 || groupedItems.length === 0) {
    return null;
  }

  return (
    <section className="py-8 md:pb-0 md:py-12 w-full">
        {/* Title */}
        {showTitle && title && (
          <div className="container-fluid">
            <h2 className="mb-4 text-base md:text-[24px] font-normal text-center leading-normal md:leading-[30px]">
              <AppLink
                to={link}
                shouldRefresh={data.shouldRefresh}
                eventName="Browse All Collections Clicked"
                eventProperties={{
                  'From Source': 'Home Page Title Link',
                }}
                className="hover:opacity-80 transition-opacity font-primary text-[#9D7792] md:text-[#032C5E]"
              >
                {title}
              </AppLink>
            </h2>
          </div>
        )}

      {/* Desktop: Three items side by side */}
      {desktopItems.length > 0 && (
        <div className="hidden md:flex md:gap-4 container-fluid pt-[50px]">
          {desktopItems.map((item, index) => (
            <div
              key={`desktop-${item.title}-${index}`}
              className={[
                'relative flex-1 flex flex-col transform-gpu will-change-transform',
                'transition-[opacity,transform] duration-700 ease-out',
                'motion-reduce:transition-none motion-reduce:transform-none motion-reduce:opacity-100',
                // Triggered by SectionRenderer's data-inview="true" (desktop only).
                'opacity-0 translate-y-6',
                index === 0 ? '-translate-x-6' : '',
                index === 2 ? 'translate-x-6' : '',
                'md:group-data-[inview=true]:opacity-100 md:group-data-[inview=true]:translate-y-0 md:group-data-[inview=true]:translate-x-0',
                index === 1 ? 'md:top-[-50px]' : '',
              ].join(' ')}
            >
            <CollectionCardContent item={item} variant="desktop" />
            {/* Button below middle banner (index 1) */}
            {index === 1 && data.btn && (
              <div className="mt-4 flex justify-center">
                <AppLink
                  to={link}
                  eventName="Browse All Collections Clicked"
                  eventProperties={{
                    'From Source': 'Home Page',
                  }}
                  className="inline-flex items-center justify-center px-6 py-2 rounded-lg bg-[#FEEEF1] text-[#9D7792] font-medium text-sm hover:opacity-90 transition-opacity"
                >
                  {data.btn}
                </AppLink>
              </div>
            )}
            </div>
          ))}
        </div>
      )}

      {/* Mobile: Carousel - full bleed, 80vw width per slide */}
      {groupedItems.length > 0 && (
        <Carousel
          onUserSelect={(selectedIndex) => {
            if (!analytics || !isInitialized) return;
            const group = groupedItems[selectedIndex];
            const bannerName = group?.[0]?.title ?? 'Collections';

            try {
              analytics.trackEvent('Home Page Banner Viewed', {
                'Banner Name': bannerName,
                'Banner Position': selectedIndex + 1,
                'Banner Type': 'collections',
                'Section Name': 'Collections Carousel',
              });
            } catch (error) {
              console.error('Error tracking Home Page Banner Viewed:', error);
            }
          }}
          opts={{
            align: 'center',
            loop: false,
            slidesToScroll: 1,
            containScroll: false,
            startIndex: initialSlideIndex,
          }}
          className="w-full md:hidden"
        >
          <CarouselContent className="-ml-0 gap-2.5">
            {groupedItems.map((group, groupIndex) => (
              <CarouselItem
                key={`group-${groupIndex}`}
                className="pl-0 basis-auto flex-shrink-0"
                style={{ width: '80vw', minWidth: '80vw', maxWidth: '80vw' }}
              >
                  {/* Each slide contains items stacked vertically */}
                  <div className="flex flex-col gap-3 md:gap-4 h-full pb-2">
                    {group.map((item, itemIndex) => (
                      <CollectionCardContent
                        key={`${item.title}-${itemIndex}`}
                        item={item}
                        variant="mobile"
                      />
                    ))}
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
      )}
    </section>
  );
}
